import { Injectable } from '@angular/core';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { MonUtilsService } from '@client/app/blocks/service/mon-utils/mon-utils.service';
import { ChangeTypeType, HistoryHelperService } from '@client/app/blocks/helpers/history.helper.service';
import { MonNumberService } from '@monsido/core/locale/services/mon-number.service';

@Injectable({
    providedIn: 'root',
})
export class HistoryPrepareService {
    private readonly percentageParam = '0.[00]%';
    private readonly seoIssuesStack = ['seo_errors_alerts_count', 'seo_errors_warnings_count', 'seo_errors_infos_count'];
    private readonly qaIssuesStack = ['dead_links_count', 'dead_images_count', 'spelling_errors_confirmed_count'];
    private readonly accessibilityIssuesStack = ['accessiblity_issues_count'];
    private readonly seoOpportunityOptions = [
        { attr: 'seo_errors_alerts_count', color: 'text-danger' },
        { attr: 'seo_errors_warnings_count', color: 'text-warning' },
        { attr: 'seo_errors_infos_count', color: 'text-info' },
        { attr: 'seo_errors_technical_count', color: 'text-primary' },
    ];

    constructor (
        private complianceService: ComplianceService,
        private activeFeatureService: ActiveFeatureService,
        private monUtilsService: MonUtilsService,
        private historyHelperService: HistoryHelperService,
        private monNumberService: MonNumberService,
    ) { }

    prepareAccessibilityHistory (history: any[]): any[] {
        const result: any[] = [];
        const allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');
        const moduleAndType = 'accessibility';

        for (const story of history) {
            const complianceAr = this.complianceService.accessibilityHistoryCompliance(story);
            const entry = this.getEntry(story, history, complianceAr, this.accessibilityIssuesStack, allowVerticalAverage, moduleAndType, 'accessibility');

            entry.pages_with_accessibility_errors_count = story.pages_with_accessibility_errors_count;
            entry.accessibility_checks = story.accessibility_checks || {};
            entry.accessibility_issues_count = this.formatNumber(story.accessibility_issues_count);
            result.push(entry);
        }
        return result;
    }

    prepareQAHistory (history: any[]): any[] {
        const result: any[] = [];
        const allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');

        for (const story of history) {
            const complianceAr = this.complianceService.qaHistoryCompliance(story);
            const entry = this.getEntry(story, history, complianceAr, this.qaIssuesStack, allowVerticalAverage, 'qa');

            entry.documents_count = this.formatNumber(story.documents_count);
            entry.dead_links_count = this.formatNumber(story.dead_links_count);
            entry.dead_images_count = this.formatNumber(story.dead_images_count);
            entry.spelling_errors_confirmed_count = this.formatNumber(story.spelling_errors_confirmed_count);

            result.push(entry as any);
        }
        return result;
    }

    prepareSEOHistory (history: any[]): any[] {
        const result: any[] = [];
        const allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');

        for (const story of history) {
            const complianceAr = this.complianceService.seoHistoryCompliance(story);
            const entry = this.getEntry(story, history, complianceAr, this.seoIssuesStack, allowVerticalAverage, 'seo');

            entry.documents_count = this.formatNumber(story.documents_count);
            entry.doughnutTotalCount = entry.totalCount;
            entry.opportunities = {};
            this.seoOpportunityOptions.forEach(opportunity => {
                entry.opportunities[opportunity.attr] = this.formatNumber(story[opportunity.attr]);
            });

            result.push(entry as any);
        }
        return result;
    }

    private getEntry (story: any, history: any[], complianceAr: any, issuesStack: string[], allowVerticalAverage: boolean, module: string, type?: ChangeTypeType): any {
        const countAndError = this.getCountAndError(complianceAr, 100, null);

        const entry = {
            id: story.id,
            post_processing_done_at: story.post_processing_done_at,
            date: story.date,
            page_count: this.formatNumber(story.page_count),
            error: countAndError.error,
            totalCount: countAndError.totalCount,
            changePercentageValue: this.formatNumber(
                this.historyHelperService.getChangePercentageValue(story.id, history, issuesStack, type) / 100,
                this.percentageParam,
            ),
            hasCompliance: countAndError.error !== null,
            changeValueColorClass: this.getChangeValueColorClass(story, history, issuesStack, type),
            changeValueIconClass: this.getChangeValueIconClass(story, history, issuesStack, type),
            compliancePercentage: countAndError.error !== null ? this.monUtilsService.withinHundred(1 - countAndError.error / countAndError.totalCount) * 100 : 0,
        };

        if (allowVerticalAverage && story.vertical_scores && entry.hasCompliance) {
            const comparisonToAverage = Math.round(entry.compliancePercentage - story.vertical_scores[module]) / 100;
            (entry as any).comparisonToAverage = this.formatNumber(comparisonToAverage, this.percentageParam);
            (entry as any).comparisonToAverageStyle = this.monUtilsService.getTextStyleByNumberValue(comparisonToAverage);
        }

        return entry;
    }

    private getCountAndError (complianceAr: any, totalCount: number, error: number | null): { totalCount: number; error: number | null } {
        const result = { totalCount, error };
        if (Array.isArray(complianceAr)) {
            result.error = complianceAr[1];
        }
        return result;
    }

    private getChangeValueColorClass (story: any, history: any[], issuesStack: string[], type?: ChangeTypeType): Record<string, boolean> {
        const value = this.historyHelperService.getChangeValue(story.id, history, issuesStack, type);
        return this.historyHelperService.getChangeColorClass(value);
    }

    private getChangeValueIconClass (story: any, history: any[], issuesStack: string[], type?: ChangeTypeType): Record<string, boolean> {
        const value = this.historyHelperService.getChangeValue(story.id, history, issuesStack, type);
        return this.historyHelperService.getChangeIconClass(value);
    }

    private formatNumber (value: number, format: string = '1'): string {
        return this.monNumberService.transformNumber(value, format) || '';
    }
}
